import { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { encode, decode } from '@/base64';
import { deep_copy, newUid } from '@/util';
import { Button, Switch, InputScore } from '@teo/components';

import { WidgetHeader } from './WidgetHeader';
import { initialOpenVraag } from '@/components/Froala/widgets/OpenQuestionEdit.jsx';
import { initialMeerkeuze } from '@/components/Froala/widgets/MultipleChoiceEdit.jsx';
import { initialFileUpload } from '@/components/Froala/widgets/UploadEdit.jsx';
import { initialInvulVraag } from '@/components/Froala/widgets/InvulVraagEdit.jsx';
import { initialOrderVraag } from '@/components/Froala/widgets/OrderEdit.jsx';
import { initialLink } from '@/components/Froala/widgets/ConnectEdit.jsx';
import { initialMatrixVraag2 } from '@/components/Froala/widgets/MatrixEdit.jsx';
import { initialNameImage2 } from '@/components/Froala/widgets/NamePartsQuestion.jsx';
import { initialSortGroups } from '@/components/Froala/widgets/SortGroupsEdit.jsx';
import { initialShowOnPicture } from '@/components/Froala/widgets/ShowOnPictureEdit.jsx';
import { WidgetOptions } from './widgetOptions/WidgetOptions';
import { getEditor } from './renderEditor';

export const initialRandomGroup = (count = 1) => {
  const items = Array.from({ length: count }, () => ({
    ...initialMeerkeuze(),
    uid: newUid(20),
  }));

  return {
    score: 1,
    type: '',
    items,
    showCount: Math.min(1, items.length),
    randomPick: true,
    isGroup: true,
  };
};

export const RandomGroupEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
}) => {
  const { t } = useTranslation();

  const [updateChild, setUpdateChild] = useState(newUid(10));

  const [titleWidget, setTitleWidget] = useState(
    state?.titleWidget
      ? state?.titleWidget
      : t('widgets.group_widget.random_question_group')
  );

  const [score, setScore] = useState(state.score || 0);
  const [oneAnswer, setOneAnswer] = useState(state?.oneAnswer);
  const [scorePerAnswer, setScorePerAnswer] = useState(state?.scorePerAnswer);
  const [randomOrder, setRandomOrder] = useState(
    state.randomOrder !== undefined ? state.randomOrder : true
  );
  const [excludeScore, setExcludeScore] = useState(
    state.excludeScore !== undefined ? state.excludeScore : false
  );

  const [items, setItems] = useState(() => {
    const existing = Array.isArray(state?.items) ? state.items : [];
    return existing.map((x) => ({ ...x, uid: x.uid || newUid(20) }));
  });

  const [showCount, setShowCount] = useState(() => {
    const n = typeof state?.showCount === 'number' ? state.showCount : 2;
    return Math.max(1, Math.min(n, items.length || 1));
  });

  const itemStateFnsRef = useRef({});

  const showCountOptions = useMemo(() => {
    const max = Math.max(1, items.length);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [items.length]);

  let groupType;
  let initial;
  switch (state.type) {
    case 'InvulVraagGroup':
      groupType = 'InvulVraag';
      initial = initialInvulVraag;
      break;
    case 'MeerKeuzeGroup':
      groupType = 'MeerKeuze';
      initial = initialMeerkeuze;
      break;
    case 'OpenVraagGroup':
      groupType = 'OpenVraag';
      initial = initialOpenVraag;
      break;
    case 'NameImage2Group':
      groupType = 'NameImage2';
      initial = initialNameImage2;
      break;
    case 'MatrixVraag2Group':
      groupType = 'MatrixVraag2';
      initial = initialMatrixVraag2;
      break;
    case 'OrderVraagGroup':
      groupType = 'OrderVraag';
      initial = initialOrderVraag;
      break;
    case 'LinkGroup':
      groupType = 'Link';
      initial = initialLink;
      break;
    case 'FileUploadGroup':
      groupType = 'FileUpload';
      initial = initialFileUpload;
      break;
    case 'SortGroupsGroup':
      groupType = 'SortGroups';
      initial = initialSortGroups;
      break;
    case 'ShowOnPictureGroup':
      groupType = 'ShowOnPicture';
      initial = initialShowOnPicture;
      break;
  }

  const getState = async () => {
    const newState = deep_copy(state);
    newState.type = state.type;
    newState.showCount = Math.max(1, Math.min(showCount, items.length || 1));

    const resolvedItems = [];
    for (const item of items) {
      const fn = itemStateFnsRef.current[item.uid];
      if (fn) {
        const next = await fn();
        resolvedItems.push({ ...next, uid: item.uid, type: groupType });
      } else {
        resolvedItems.push({ ...item, uid: item.uid, type: groupType });
      }
    }
    newState.items = resolvedItems;
    newState.titleWidget = titleWidget;

    newState.score = score;
    newState.oneAnswer = oneAnswer;
    newState.scorePerAnswer = scorePerAnswer;
    newState.randomOrder = randomOrder;
    newState.excludeScore = excludeScore;
    newState.isGroup = true;

    return newState;
  };
  setStateFn && setStateFn(getState);

  const addItem = () => {
    const next = { ...initial(), uid: newUid(20) };
    setItems((prev) => [...prev, next]);
    setShowCount((prev) => Math.max(1, Math.min(prev, items.length + 1)));
    onModified && onModified();
  };

  const removeItem = (uid) => {
    setItems((prev) => prev.filter((x) => x.uid !== uid));
    delete itemStateFnsRef.current[uid];
    setShowCount((prev) => Math.max(1, Math.min(prev, items.length - 1)));
    onModified && onModified();
  };

  const handleFocus = (uid) => {
    const el = document.getElementById(`wg-g-${uid}`);
    if (!el) return;

    el.classList.add('!border-secondary-04');
  };

  const handleBlur = (uid) => {
    const el = document.getElementById(`wg-g-${uid}`);
    if (!el) return;

    el.classList.remove('!border-secondary-04');
  };
  const handleFocusParent = (uid) => {
    const el = document.getElementById(`widget-parent-${uid}`);
    if (!el) return;

    el.classList.add('border-grey-06');
    el.classList.add('!bg-white');
  };

  const handleBlurParent = (uid) => {
    const el = document.getElementById(`widget-parent-${uid}`);
    if (!el) return;

    el.classList.remove('border-grey-06');
    el.classList.remove('!bg-white');
  };

  const hendleUngroup = async () => {
    const groupState = await getState();

    const itemsToUngroup = Array.isArray(groupState?.items)
      ? groupState.items
      : [];
    if (!itemsToUngroup.length) return;

    const nextWidgets = itemsToUngroup.map((it) => {
      const { titleWidgetGroup, ...cleanItem } = deep_copy(it); // CHANGED

      return {
        ...cleanItem,
        uid: newUid(20),
        type: groupType,
        group: false,
      };
    });

    window.dispatchEvent(
      new CustomEvent('froala:ungroup-widget', {
        detail: { oldUid: state.uid, nextWidgets },
      })
    );
  };

  return (
    <div
      className="flex w-full flex-col gap-4 lg:p-4"
      style={{
        maxWidth:
          groupType == 'MatrixVraag2'
            ? 'calc(100% - 52px)'
            : 'calc(100vw - 32px)',
      }}
      data-state={encode(JSON.stringify({ ...state, titleWidget }))}
      id={state?.uid}
    >
      <div
        className="rounded-lg border border-dashed p-4 hover:bg-grey-01 focus:bg-grey-04"
        id={`widget-parent-${state?.uid}`}
        onFocus={() => handleFocusParent(state?.uid)}
        onBlur={() => handleBlurParent(state?.uid)}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-row items-center gap-3">
            <div className="w-full">
              <WidgetHeader
                index={'group'}
                titleWidget={titleWidget}
                setTitleWidget={setTitleWidget}
              />
            </div>

            <div className="flex w-full flex-row items-center lg:mt-0 lg:w-auto">
              <div className="mr-2 min-w-max">
                {t('widgets.group_widget.show_question')}
              </div>
              <InputScore
                value={showCount}
                minScore={1}
                maxScore={showCountOptions?.length}
                onChange={(x) => {
                  setShowCount(x || 1);
                  onModified && onModified();
                }}
              />
            </div>
            <div>
              <Button variant="outline" onClick={() => hendleUngroup()}>
                {t('widgets.group_widget.ungroup')}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {items.map((item, idx) => {
              let Editor = getEditor(item);
              let widgetState = {
                ...item,
                score: score,
                excludeScore: excludeScore,
                type: groupType,
                group: true,
                titleWidgetGroup: titleWidget,
              };
              if (groupType == 'MeerKeuze')
                widgetState = {
                  ...widgetState,
                  oneAnswer: oneAnswer,
                  scorePerAnswer: false,
                  randomOrder: randomOrder,
                };

              if (groupType == 'Link')
                widgetState = {
                  ...widgetState,
                  randomOrder: randomOrder,
                };
              if (groupType == 'MatrixVraag2') {
                widgetState = {
                  ...widgetState,
                  radioMode: oneAnswer,
                  radioScoreRow: false,
                };
              }

              return (
                <div
                  key={item.uid}
                  className="flex flex-row items-center rounded-lg border border-white"
                  id={`wg-g-${item.uid}`}
                  // onFocus={handleFocus(item.uid)}
                  onFocus={() => handleFocus(item.uid)}
                  onBlur={() => handleBlur(item.uid)}
                >
                  <Editor
                    key={updateChild}
                    state={widgetState}
                    index={idx + 1}
                    onModified={onModified}
                    setStateFn={(fn) => {
                      itemStateFnsRef.current[item.uid] = fn;
                    }}
                  />
                  <div className="flex flex-col gap-3">
                    <WidgetOptions
                      widget={null}
                      placeholder={null}
                      state={{ ...item, group: true }}
                      undo={null}
                      chatGPT={
                        state?.type == 'MeerKeuze' ||
                        state?.type == 'OpenVraag' ||
                        state?.type == 'Link' ||
                        state?.type == 'OrderVraag'
                      }
                      delItem={() => {
                        handleBlurParent(state?.uid);
                        removeItem(item.uid);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-row items-center justify-between">
            <Button variant="secondary" onClick={() => addItem()}>
              {t('widgets.group_widget.add_question')}
            </Button>
          </div>
        </div>

        <div>
          <div className="mt-4 flex flex-col border-grey-02">
            {state?.type === 'MatrixVraag2Group' && (
              <div className="mt-4 flex flex-row items-center border-t border-b border-grey-02 py-3">
                <Switch
                  checked={oneAnswer}
                  onChange={(isCheck) => {
                    setOneAnswer(isCheck);
                    setUpdateChild(newUid(10));
                  }}
                />
                <div className="mr-4 px-4">
                  {t('widgets.edit.matrix_question.one_answer_per_row')}
                </div>
                {/* <Switch
                  checked={scorePerAnswer}
                  onChange={(isCheck) => {
                    setScorePerAnswer(isCheck);
                    setUpdateChild(newUid(10));
                  }}
                />
                <div className="px-4">
                  {t('widgets.edit.matrix_question.score_per_row')}
                </div> */}
              </div>
            )}
            {state?.type === 'MeerKeuzeGroup' && (
              <div className="flex flex-row flex-wrap items-center border-t py-3 lg:flex-nowrap">
                <div className="flex flex-row items-center">
                  <Switch
                    checked={oneAnswer}
                    onChange={(isCheck) => {
                      setOneAnswer(isCheck);
                      setUpdateChild(newUid(10));
                    }}
                  />
                  <div className="mr-4 px-4">
                    {t('widgets.widgets_edit.one_answer')}
                  </div>
                </div>
                {/* <div className="mt-5 flex w-full flex-row items-center sm:mt-0 sm:w-auto">
                  <Switch
                    checked={scorePerAnswer}
                    onChange={(isCheck) => {
                      setScorePerAnswer(isCheck);
                      setUpdateChild(newUid(10));
                    }}
                  />
                  <div className="mr-4 px-4">
                    {t('widgets.widgets_edit.score_answer')}
                  </div>
                </div> */}
              </div>
            )}
            <div className="flex flex-row items-center border-t border-grey-02 pt-4 sm:pt-2">
              <div className="flex w-full flex-row items-center sm:mt-0 sm:w-auto">
                <Switch
                  checked={excludeScore}
                  onChange={(isCheck) => {
                    setExcludeScore(isCheck);
                    if (isCheck) {
                      setScore(0);
                    }
                  }}
                />
                <div className="mr-4 border-r-2 border-grey-02 px-4">
                  {t('widgets.edit.exclude_score')}
                </div>
              </div>
              <div className="flex w-full flex-row items-center lg:mt-0 lg:w-auto">
                <div className="mr-2">
                  {t('widgets.widgets_edit.max_score')}
                </div>
                <InputScore
                  value={score}
                  minScore={0}
                  onChange={(value) => {
                    !scorePerAnswer && setScore(value);
                  }}
                  disabled={excludeScore || scorePerAnswer}
                />
              </div>
            </div>
            {(state?.type === 'MeerKeuzeGroup' ||
              state?.type === 'LinkGroup') && (
              <div className="mt-4 flex flex-row items-center border-t border-grey-02 pt-[10px] pb-1 sm:mt-2 sm:pt-3">
                <Switch
                  checked={randomOrder}
                  onChange={(isCheck) => {
                    setRandomOrder(isCheck);
                  }}
                />
                <div className="mr-4 px-4">
                  {t('widgets.edit.order_message')}
                </div>
              </div>
            )}
          </div>
          <p className="mt-2 text-sm text-grey-05">
            {t('widgets.group_widget.m_settings_group')}
          </p>
        </div>
      </div>
    </div>
  );
};
